import { TGetIdea } from "src/components/ideaHub/ideaHub.types";
import 'bootstrap/dist/css/bootstrap.min.css';
import cx from './ideareport.module.scss';
import { useEffect } from 'react';
import GradientButton from "src/components/common/formFields/gradient-button/GradientButton";
import { useNavigate } from "react-router-dom";

interface AccordionItem {
    title: string;
    content: string;
}

export default function IdeaReport({ idea: ideaData }: { idea: TGetIdea | null }) {
    const navigate = useNavigate();
    const idea = ideaData?.idea;
    const isReportNotGenerated = idea?.validationReport?.includes('Please provide appropriate input to generate an actionable report.');

    // Initialize Bootstrap accordion functionality
    useEffect(() => {
        // Dynamically import Bootstrap JS to ensure it's loaded
        import('bootstrap/dist/js/bootstrap.bundle.min.js').then(() => {
            // Bootstrap is now loaded and should work
        }).catch(err => {
            console.error('Failed to load Bootstrap JS:', err);
        });
    }, []);

    // Function to parse validation report and create accordion items
    const getAccordionItems = (): AccordionItem[] => {
        if (isReportNotGenerated) {
            return [{
                title: 'Overall Feedback',
                content: 'Please provide appropriate input to generate an actionable report!'
            }];
        }
        
        if (!idea?.validationReport) {
            return [{
                title: 'Validation Report',
                content: 'The report has not been generated yet!'
            }];
        }

        try {
            // First try to parse as JSON
            const reportData = JSON.parse(idea.validationReport);

            // Define section titles and their corresponding keys for JSON format
            const sections = [
                { key: 'overallFeedback', title: 'Overall Feedback' },
                { key: 'marketFeasibility', title: 'Market Feasibility' },
                { key: 'idealCustomerAndProfile', title: 'Ideal Customer & Profile' },
                { key: 'competitiveAnalysis', title: 'Competitive Analysis' },
                { key: 'marketSizeAndGrowthPotential', title: 'Market Size & Growth Potential' },
                { key: 'goToMarketStrategy', title: 'Go-to-Market Strategy' },
                { key: 'risksAndMitigationStrategies', title: 'Risks & Mitigation Strategies' },
                { key: 'financialViability', title: 'Financial Viability' },
                { key: 'oneHighValueRecommendation', title: 'High-Value Recommendation' },
                { key: 'finalRequest', title: 'Final Request' }
            ];
            
            return sections
                .filter(section => reportData[section.key])
                .map(section => ({
                    title: section.title,
                    content: reportData[section.key]
                }));
        } catch (err: unknown) {
            if (err instanceof Error) {
                console.log('Caught error:', err.message);
                console.log('Stack trace:', err.stack);
            } else {
                console.log('Caught unknown error:', err);
            }
            return parseStringFormat(idea.validationReport);
        }
    };

    // Function to parse string format validation report
    const parseStringFormat = (reportString: string): AccordionItem[] => {
        return [{
            title: 'Validation Report',
            content: reportString
        }];
    };

    // Enhanced function to render markdown/HTML content with better bullet point handling
    const renderContent = (content: string) => {
        // Process bullet points that start with •
        const processedContent = content.replace(/• ([^:]+):\s*([^\n•]+)/g, (match, title, content) => {
            return `<div class="${cx.bulletPoint}"><div class="${cx.bulletTitle}">• ${title}:</div><div class="${cx.bulletContent}">${content}</div></div>`;
        });

        // Simple markdown to HTML conversion for basic formatting
        const htmlContent = processedContent
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
            .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
            .replace(/\n\n/g, '</p><p>') // Paragraph breaks
            .replace(/\n/g, '<br/>') // Line breaks
            .replace(/#{3}\s(.*)/g, '<h3>$1</h3>') // H3 headers
            .replace(/#{2}\s(.*)/g, '<h2>$1</h2>') // H2 headers
            .replace(/#{1}\s(.*)/g, '<h1>$1</h1>'); // H1 headers

        // Wrap content in paragraph tags if it doesn't start with a heading
        const finalContent = htmlContent.startsWith('<h') ? htmlContent : `<p style={font-size: '12px'}>${htmlContent}</p>`;

        return <div dangerouslySetInnerHTML={{ __html: finalContent }} />;
    };

    const accordionItems = getAccordionItems();

    return (
        <div className={cx.accordionContainer}>
            {accordionItems.length > 0 ? (
                <div className={`accordion ${cx.customAccordion}`} id="ideaReportAccordion">
                    {accordionItems.map((item, index) => (
                        <div className={`accordion-item ${cx.accordionItem}`} key={index}>
                            <h2 className={`accordion-header ${cx.accordionHeader}`} id={`heading${index}`}>
                                <button
                                    className={`accordion-button ${cx.accordionButton} ${index !== 0 ? 'collapsed' : ''}`}
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#collapse${index}`}
                                    aria-expanded={index === 0 ? 'true' : 'false'}
                                    aria-controls={`collapse${index}`}
                                >
                                    {item.title}
                                </button>
                            </h2>
                            <div
                                id={`collapse${index}`}
                                className={`accordion-collapse collapse ${cx.accordionCollapse} ${index === 0 ? 'show' : ''}`}
                                aria-labelledby={`heading${index}`}
                                data-bs-parent="#ideaReportAccordion"
                            >
                                <div className={`accordion-body ${cx.accordionBody}`}>
                                    {renderContent(item.content)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                // Fallback to display raw content if no sections found
                <div className={`card ${cx.fallbackCard}`}>
                    <div className={`card-body ${cx.cardBody}`}>
                        {renderContent(idea?.validationReport || '')}
                    </div>
                </div>
            )}
            <div className={cx.buttonContainer}>
                <GradientButton onClick={() => navigate(`/idea-hub?id=${idea?.id}`)}>
                    Update & Validate with AI
                </GradientButton>
            </div>
        </div>
    );
}
